import React, { useState, useEffect } from "react";
import cmsApi from "../../../services/cmsApi";
import { getImageUrl } from "../../../utils/getImageUrl";
import { getCatalog } from "../../../utils/shortCoursesCatalog";

const ICONS = {
  mode: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  clock: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  fee: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

function Stat({ icon, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-600">
      <span className="text-custom-pink">{icon}</span>
      {value}
    </div>
  );
}

// One subject (e.g. Business Management) — its own Overview/Courses tabs.
// `reverse` flips which side the Overview image sits on, so subjects
// alternate left/right down the page instead of repeating the same layout.
function SubjectBlock({ category, reverse }) {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div>
      <div className="mb-6">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{category.tag}</span>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">{category.name}</h3>
      </div>

      <div className="mb-8 inline-flex flex-wrap gap-1 rounded-xl bg-gray-100 p-1">
        {[
          { id: "overview", label: "Overview" },
          { id: "courses", label: "Courses" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? "bg-white text-custom-pink shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className={reverse ? "md:order-2" : ""}>
            <p className="text-gray-600 leading-relaxed text-lg">
              {category.heroIntro || "Overview coming soon."}
            </p>
          </div>
          <div className={`rounded-2xl overflow-hidden shadow-md ${reverse ? "md:order-1" : ""}`}>
            {category.badge_image ? (
              <img
                src={getImageUrl(category.badge_image)}
                alt={category.name}
                className="w-full h-64 md:h-80 object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-64 md:h-80 bg-gradient-to-br from-custom-pink/10 to-gold/10" />
            )}
          </div>
        </div>
      )}

      {activeTab === "courses" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {category.courses.map((course) => (
            <div
              key={course.slug}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col hover:shadow-md hover:border-custom-pink/30 transition-all duration-300"
            >
              {course.image ? (
                <img
                  src={getImageUrl(course.image)}
                  alt={course.name}
                  className="w-full h-36 object-cover flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-custom-pink/10 to-gold/10 flex-shrink-0" />
              )}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{course.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">
                  {course.overview || "Course overview coming soon."}
                </p>
                <div className="grid grid-cols-2 gap-y-2 gap-x-3 pt-4 border-t border-gray-100">
                  <Stat icon={ICONS.mode} value={course.mode} />
                  <Stat icon={ICONS.calendar} value={course.duration} />
                  <Stat icon={ICONS.clock} value={course.hours} />
                  <Stat icon={ICONS.fee} value={course.fee} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// "Available Subjects" section — every short-course subject (Business
// Management, AI Powered Graphics, etc.) listed on this one page, each with
// its own Overview/Courses tabs. No separate per-subject page/navigation —
// content is CMS-editable via the "Short Courses Catalog" program in the
// dashboard; falls back to seed data.
const ShortCoursesList = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cmsApi
      .getProgramBySlug("short-courses-catalog")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subjects…</p>
        </div>
      </div>
    );
  }

  const categories = getCatalog(program?.ciscoCcnpSection ?? null);

  return (
    <section className="relative py-16 md:py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-12 right-10 w-72 h-72 bg-custom-pink/10 rounded-full blur-3xl" />
        <div className="absolute bottom-12 left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-custom-pink leading-tight">
            Available Subjects
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto rounded-full mt-4" />
          <p className="mt-4 text-gray-600 text-base md:text-lg">
            Explore our short courses by category.
          </p>
        </div>

        <div className="space-y-16 md:space-y-20">
          {categories.map((cat, i) => (
            <SubjectBlock key={cat.slug} category={cat} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShortCoursesList;
